// Free self-assessment screenings shown on /self-assessment.
// Each screening is scored on its own (as the clinical tools are designed), never added together.
//
// Sources:
//  - PHQ-9 & GAD-7: Spitzer, Williams, Kroenke et al. — free to use, no permission required.
//  - AUDIT: World Health Organization (2001) — free to use.
//  - DAST-10: Harvey A. Skinner (1982), Addiction Research Foundation — reproduction is permitted
//    for non-commercial clinical use with acknowledgement; confirm with the client before going live.
//  - "Concern for a loved one" is Unity Health Centre's own guide (not a validated clinical scale).
//
// Field guide:
//  options:      shared answer options [{ label, value }]; a question can override with its own `options`
//  ranges:       [{ max, level, tone, desc, advice }] — first range whose `max` >= score is used
//  tone:         "low" | "mild" | "moderate" | "high" | "severe" (controls the result colour)
//  safetyItem:   index of a question that triggers the urgent-help message when its answer value > 0
//  recommend:    serviceIds / packageIds shown as next steps (from data/services.js & data/packages.js)

const frequency = [
  { label: "Not at all", value: 0 },
  { label: "Several days", value: 1 },
  { label: "More than half the days", value: 2 },
  { label: "Nearly every day", value: 3 },
];

const yesNo = [
  { label: "No", value: 0 },
  { label: "Yes", value: 1 },
];

const assessments = [
  {
    id: "depression",
    title: "Depression Check",
    tool: "PHQ-9",
    icon: "icon-heart",
    summary:
      "Feeling down, tired or losing interest in things? A 2-minute check for depression.",
    minutes: 2,
    intro:
      "Over the last 2 weeks, how often have you been bothered by any of the following problems?",
    options: frequency,
    questions: [
      "Little interest or pleasure in doing things",
      "Feeling down, depressed, or hopeless",
      "Trouble falling or staying asleep, or sleeping too much",
      "Feeling tired or having little energy",
      "Poor appetite or overeating",
      "Feeling bad about yourself — or that you are a failure or have let yourself or your family down",
      "Trouble concentrating on things, such as reading the newspaper or watching television",
      "Moving or speaking so slowly that other people could have noticed — or the opposite, being so fidgety or restless that you have been moving around a lot more than usual",
      "Thoughts that you would be better off dead, or of hurting yourself in some way",
    ],
    safetyItem: 8,
    maxScore: 27,
    ranges: [
      {
        max: 4,
        level: "Minimal symptoms",
        tone: "low",
        desc: "Your answers suggest minimal or no signs of depression right now.",
        advice:
          "Keep looking after your sleep, activity and relationships. Take this check again if things change.",
      },
      {
        max: 9,
        level: "Mild depression",
        tone: "mild",
        desc: "Your answers suggest mild symptoms of depression.",
        advice:
          "Talking to a counselor early can stop symptoms from growing. Our team offers confidential counseling.",
      },
      {
        max: 14,
        level: "Moderate depression",
        tone: "moderate",
        desc: "Your answers suggest moderate symptoms that are likely affecting daily life.",
        advice:
          "We recommend a consultation with a psychiatrist or clinical psychologist for a proper assessment.",
      },
      {
        max: 19,
        level: "Moderately severe depression",
        tone: "high",
        desc: "Your answers suggest moderately severe symptoms of depression.",
        advice:
          "Please book a psychiatric consultation soon. Effective treatment is available and recovery is possible.",
      },
      {
        max: 27,
        level: "Severe depression",
        tone: "severe",
        desc: "Your answers suggest severe symptoms of depression.",
        advice:
          "Please contact us today for an urgent consultation. You do not have to face this alone.",
      },
    ],
    recommend: { serviceIds: [1, 3], packageIds: [4] },
  },
  {
    id: "anxiety",
    title: "Anxiety Check",
    tool: "GAD-7",
    icon: "icon-brain",
    summary:
      "Worrying a lot, feeling nervous or unable to relax? A 2-minute check for anxiety.",
    minutes: 2,
    intro:
      "Over the last 2 weeks, how often have you been bothered by the following problems?",
    options: frequency,
    questions: [
      "Feeling nervous, anxious, or on edge",
      "Not being able to stop or control worrying",
      "Worrying too much about different things",
      "Trouble relaxing",
      "Being so restless that it is hard to sit still",
      "Becoming easily annoyed or irritable",
      "Feeling afraid, as if something awful might happen",
    ],
    maxScore: 21,
    ranges: [
      {
        max: 4,
        level: "Minimal anxiety",
        tone: "low",
        desc: "Your answers suggest minimal anxiety right now.",
        advice:
          "Healthy routines, exercise and good sleep help keep stress in check.",
      },
      {
        max: 9,
        level: "Mild anxiety",
        tone: "mild",
        desc: "Your answers suggest mild anxiety.",
        advice:
          "Simple coping skills learned in counseling can make a real difference.",
      },
      {
        max: 14,
        level: "Moderate anxiety",
        tone: "moderate",
        desc: "Your answers suggest moderate anxiety that may be affecting daily life.",
        advice:
          "We recommend an assessment with a clinical psychologist or psychiatrist.",
      },
      {
        max: 21,
        level: "Severe anxiety",
        tone: "severe",
        desc: "Your answers suggest severe anxiety.",
        advice:
          "Please book a consultation soon — anxiety responds well to treatment.",
      },
    ],
    recommend: { serviceIds: [3, 1], packageIds: [4] },
  },
  {
    id: "alcohol",
    title: "Alcohol Use Check",
    tool: "AUDIT",
    icon: "icon-drugs",
    summary:
      "Worried about drinking — yours or how much it affects your life? The WHO alcohol check.",
    minutes: 3,
    intro: "Please answer about your alcohol use during the past 12 months.",
    questions: [
      {
        text: "How often do you have a drink containing alcohol?",
        options: [
          { label: "Never", value: 0 },
          { label: "Monthly or less", value: 1 },
          { label: "2 to 4 times a month", value: 2 },
          { label: "2 to 3 times a week", value: 3 },
          { label: "4 or more times a week", value: 4 },
        ],
      },
      {
        text: "How many drinks containing alcohol do you have on a typical day when you are drinking?",
        options: [
          { label: "1 or 2", value: 0 },
          { label: "3 or 4", value: 1 },
          { label: "5 or 6", value: 2 },
          { label: "7, 8 or 9", value: 3 },
          { label: "10 or more", value: 4 },
        ],
      },
      {
        text: "How often do you have six or more drinks on one occasion?",
        options: "monthly",
      },
      {
        text: "How often during the last year have you found that you were not able to stop drinking once you had started?",
        options: "monthly",
      },
      {
        text: "How often during the last year have you failed to do what was normally expected of you because of drinking?",
        options: "monthly",
      },
      {
        text: "How often during the last year have you needed a first drink in the morning to get yourself going after a heavy drinking session?",
        options: "monthly",
      },
      {
        text: "How often during the last year have you had a feeling of guilt or remorse after drinking?",
        options: "monthly",
      },
      {
        text: "How often during the last year have you been unable to remember what happened the night before because of your drinking?",
        options: "monthly",
      },
      {
        text: "Have you or someone else been injured because of your drinking?",
        options: "lastYear",
      },
      {
        text: "Has a relative, friend, doctor or other health worker been concerned about your drinking or suggested you cut down?",
        options: "lastYear",
      },
    ],
    optionSets: {
      monthly: [
        { label: "Never", value: 0 },
        { label: "Less than monthly", value: 1 },
        { label: "Monthly", value: 2 },
        { label: "Weekly", value: 3 },
        { label: "Daily or almost daily", value: 4 },
      ],
      lastYear: [
        { label: "No", value: 0 },
        { label: "Yes, but not in the last year", value: 2 },
        { label: "Yes, during the last year", value: 4 },
      ],
    },
    maxScore: 40,
    ranges: [
      {
        max: 7,
        level: "Low risk",
        tone: "low",
        desc: "Your answers suggest low-risk drinking.",
        advice:
          "Stay mindful of how much and why you drink, and avoid using alcohol to cope with stress.",
      },
      {
        max: 15,
        level: "Hazardous drinking",
        tone: "moderate",
        desc: "Your drinking pattern increases the risk of harm to your health.",
        advice:
          "A short counseling session can help you cut down before problems grow.",
      },
      {
        max: 19,
        level: "Harmful drinking",
        tone: "high",
        desc: "Your answers suggest drinking is already causing harm.",
        advice:
          "We recommend a consultation with our addiction specialist for a full assessment.",
      },
      {
        max: 40,
        level: "Possible alcohol dependence",
        tone: "severe",
        desc: "Your answers suggest possible alcohol dependence.",
        advice:
          "Please do not stop suddenly on your own — withdrawal can be dangerous. Contact us for medically supervised detox and treatment.",
      },
    ],
    recommend: { serviceIds: [2, 4], packageIds: [2, 3] },
  },
  {
    id: "drugs",
    title: "Drug Use Check",
    tool: "DAST-10",
    icon: "icon-pills",
    summary:
      "Concerned about drug use — Yaba, cannabis, heroin, sleeping pills or others?",
    minutes: 2,
    intro:
      "These questions refer to the past 12 months. “Drug use” means any drug used without a prescription or more than prescribed (not alcohol or tobacco).",
    options: yesNo,
    questions: [
      "Have you used drugs other than those required for medical reasons?",
      "Do you use more than one drug at a time?",
      {
        text: "Are you always able to stop using drugs when you want to?",
        reverse: true,
      },
      "Have you had “blackouts” or “flashbacks” as a result of drug use?",
      "Do you ever feel bad or guilty about your drug use?",
      "Does your spouse (or parents) ever complain about your involvement with drugs?",
      "Have you neglected your family because of your use of drugs?",
      "Have you engaged in illegal activities in order to obtain drugs?",
      "Have you ever experienced withdrawal symptoms (felt sick) when you stopped taking drugs?",
      "Have you had medical problems as a result of your drug use (e.g. memory loss, hepatitis, convulsions, bleeding)?",
    ],
    maxScore: 10,
    ranges: [
      {
        max: 0,
        level: "No problems reported",
        tone: "low",
        desc: "Your answers do not suggest a drug-use problem.",
        advice:
          "If someone close to you is struggling, our family screening may help.",
      },
      {
        max: 2,
        level: "Low level",
        tone: "mild",
        desc: "Your answers suggest a low level of drug-related problems.",
        advice:
          "Keep an eye on your use. Counseling can help you stay in control.",
      },
      {
        max: 5,
        level: "Moderate level",
        tone: "moderate",
        desc: "Your answers suggest a moderate level of drug-related problems.",
        advice: "We recommend an assessment with our addiction team.",
      },
      {
        max: 8,
        level: "Substantial level",
        tone: "high",
        desc: "Your answers suggest substantial drug-related problems.",
        advice:
          "Structured treatment is recommended. Talk to us about detox and rehabilitation options.",
      },
      {
        max: 10,
        level: "Severe level",
        tone: "severe",
        desc: "Your answers suggest severe drug-related problems.",
        advice:
          "Please contact us soon for medically supervised detox and inpatient rehabilitation.",
      },
    ],
    recommend: { serviceIds: [2, 4, 5], packageIds: [2, 3] },
  },
  {
    id: "family",
    title: "Concern for a Loved One",
    tool: "Family guide",
    icon: "icon-user",
    summary:
      "Worried about your son, daughter, spouse or parent? Answer about what you have noticed.",
    minutes: 2,
    intro:
      "Think about the person you are worried about. Over the past few months, have you noticed any of the following?",
    options: yesNo,
    questions: [
      "Sudden changes in mood, anger or irritability",
      "Staying away from family, friends or usual activities",
      "Big changes in sleep or appetite",
      "Money or valuables going missing without explanation",
      "New friends they keep secret, or staying out late without explanation",
      "Smell of alcohol or smoke, unusual tablets, foil or syringes",
      "Hearing or seeing things others cannot, or very unusual beliefs or behaviour",
      "Talking about hopelessness, death or hurting themselves",
    ],
    safetyItem: 7,
    maxScore: 8,
    ranges: [
      {
        max: 1,
        level: "Few warning signs",
        tone: "low",
        desc: "You noticed few warning signs.",
        advice:
          "Keep talking openly with them. If you are still worried, a family counseling session can help.",
      },
      {
        max: 3,
        level: "Some warning signs",
        tone: "moderate",
        desc: "You noticed several signs that are worth taking seriously.",
        advice:
          "A family counseling session can help you understand what is happening and how to respond.",
      },
      {
        max: 8,
        level: "Many warning signs",
        tone: "severe",
        desc: "You noticed many warning signs of a mental health or substance-use problem.",
        advice:
          "Please contact us for an assessment. We can guide you on how to bring your loved one for treatment safely.",
      },
    ],
    recommend: { serviceIds: [5, 2, 1], packageIds: [2] },
  },
];

export const assessmentContent = {
  title: "Free Self-Assessment",
  subtitle:
    "Confidential mental health & addiction screening — results in 2–3 minutes",
  homeTitle: "Are You Or A Loved One Struggling?",
  homeDesc:
    "Take a free, confidential screening for depression, anxiety, alcohol or drug use. Your answers stay on your device — nothing is sent unless you ask us to call you.",
  disclaimer:
    "This screening is not a diagnosis. Only a qualified professional can diagnose a mental health or substance-use condition after a proper assessment.",
  privacy:
    "Your answers are not stored or sent anywhere unless you request a callback.",
  safety: {
    title: "Please reach out for help now",
    desc: "You mentioned thoughts of death or self-harm. You do not have to go through this alone — please talk to someone right now.",
    familyDesc:
      "You noticed talk of hopelessness or self-harm. Please do not leave them alone and contact help right away.",
    emergencyLabel: "National Emergency",
    emergencyNumber: "999",
  },
  callback: {
    title: "Talk to us — free & confidential",
    desc: "Leave your number and our care team will call you back. Your result will be shared only with our clinical team.",
  },
};

export default assessments;
